import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ProductDetailPage.css';
import boppBag01 from '../assets/bopp-bag-01.jpeg';
import boppBag02 from '../assets/bopp-bag-02.jpeg';
import boppBag03 from '../assets/bopp-bag-03.jpeg';
import ppWovenSacks1 from "../assets/pp-woven-bags-1.jpeg";
import ppWovenSacks2 from "../assets/pp-woven-bags-2.jpeg";
import ppWovenSacks3 from "../assets/pp-woven-bags-3.jpeg";
import laminatedWovenBags1 from '../assets/laminated-woven-bags-1.jpeg';
import laminatedWovenBags2 from '../assets/laminated-woven-bags-2.jpeg';
import laminatedWovenBags3 from '../assets/laminated-woven-bags-3.jpeg';
import fibcBags1 from '../assets/fibc-bags-1.png';
import fibcBags2 from '../assets/fibc-bags-2.jpg';
import fibcBags3 from '../assets/fibc-bags-3.jpg';
import ppRolls1 from '../assets/pp-rolls-1.png';
import ppRolls2 from '../assets/pp-rolls-2.png';
import ppRolls3 from '../assets/pp-rolls-3.png';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [activeTab, setActiveTab] = useState('benefits');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [productId]);

  const getThumbnailInsight = (product, index) => {
    const manufacturingLine = product.manufacturing?.[index];
    const applicationLine = product.applications?.[index];
    const benefitLine = product.benefits?.[index]?.title;

    if (manufacturingLine) {
      return manufacturingLine;
    }

    if (applicationLine) {
      return `Used in ${applicationLine}`;
    }

    if (benefitLine) {
      return benefitLine;
    }

    return `${product.title} production highlight`;
  };

  const productData = {
    'bopp-laminated-bags': {
      title: 'BOPP Laminated Bags',
      subtitle: 'High-definition reverse printed film laminated onto woven fabric',
      description: 'Ideal for FMCG, Premium Rice, and Animal Feed, requiring superior shelf appeal. Our BOPP Laminated Bags combine the strength of woven polypropylene with the premium print quality of BOPP film.',
      images: [
        { id: 1, src: boppBag01, alt: 'BOPP Laminated Bag 1' },
        { id: 2, src: boppBag02, alt: 'BOPP Laminated Bag 2' },
        { id: 3, src: boppBag03, alt: 'BOPP Laminated Bag 3' }
      ],
      benefits: [
        {
          title: 'Superior Print Quality',
          description: 'High-definition reverse printing ensures vibrant colors and sharp graphics that remain protected from scratches and handling damage.',
          icon: 'fas fa-palette'
        },
        {
          title: 'Excellent Shelf Appeal',
          description: 'Premium BOPP film provides glossy finish and superior visual appeal, making products stand out on retail shelves.',
          icon: 'fas fa-eye'
        },
        {
          title: 'Moisture Protection',
          description: 'BOPP lamination creates an effective moisture barrier, protecting contents from humidity and environmental factors.',
          icon: 'fas fa-shield-alt'
        },
        {
          title: 'Food Grade Safety',
          description: 'Compliant with food safety standards, making it ideal for FMCG products, rice, and animal feed packaging.',
          icon: 'fas fa-certificate'
        },
        {
          title: 'Durability & Strength',
          description: 'Combines the strength of woven polypropylene with the protection of BOPP film for long-lasting packaging.',
          icon: 'fas fa-dumbbell'
        },
        {
          title: 'Cost Effective',
          description: 'Efficient material usage and production process provide excellent value for premium packaging requirements.',
          icon: 'fas fa-dollar-sign'
        }
      ],
      specifications: {
        'Material': 'BOPP Film + PP Woven Fabric',
        'Printing': 'Reverse Printed BOPP',
        'Lamination': 'Extrusion Lamination',
        'Weight Range': '10kg - 50kg',
        'Dimensions': 'Customizable as per requirement',
        'Print Colors': 'Up to 8 colors',
        'Finish': 'Glossy/Matt options available'
      },
      applications: [
        'FMCG Products',
        'Premium Rice Packaging',
        'Animal Feed',
        'Food Grains',
        'Spices & Condiments',
        'Premium Consumer Goods'
      ],
      manufacturing: [
        'PP Tape Extrusion using LOHIA Dueotec Plants',
        'Fabric Weaving with 89 LOHIA Looms',
        'BOPP Film Printing with ROTO-8COL ZOOM Machine',
        'Extrusion Lamination Process',
        'Precision Cutting & Sealing',
        'Quality Control & Testing',
        'Packaging & Dispatch'
      ]
    },
    'pp-woven-bags': {
      title: 'PP Woven Sacks/Bags',
      subtitle: 'The toughest and most cost-effective packaging solution',
      description: 'Used for bulk materials in Agriculture, Construction, Chemicals, and Consumer Goods. Our PP Woven Bags offer exceptional strength and durability for heavy-duty applications.',
      images: [
        { id: 1, src: ppWovenSacks1, alt: 'PP Woven Bag 1' },
        { id: 2, src: ppWovenSacks2, alt: 'PP Woven Bag 2' },
        { id: 3, src: ppWovenSacks3, alt: 'PP Woven Bag 3' }
      ],
      benefits: [
        {
          title: 'Exceptional Strength',
          description: 'High tensile strength polypropylene tapes provide superior durability for heavy-duty applications.',
          icon: 'fas fa-dumbbell'
        },
        {
          title: 'Cost Effective',
          description: 'Most economical packaging solution for bulk materials with excellent value for money.',
          icon: 'fas fa-dollar-sign'
        },
        {
          title: 'Weather Resistant',
          description: 'Excellent resistance to UV rays, moisture, and environmental factors for outdoor storage.',
          icon: 'fas fa-sun'
        },
        {
          title: 'Reusable',
          description: 'Durable construction allows for multiple uses, reducing overall packaging costs.',
          icon: 'fas fa-recycle'
        },
        {
          title: 'Customizable',
          description: 'Available in various sizes, colors, and printing options to meet specific requirements.',
          icon: 'fas fa-cogs'
        },
        {
          title: 'Easy Handling',
          description: 'Lightweight yet strong, making them easy to handle and transport.',
          icon: 'fas fa-hand-paper'
        }
      ],
      specifications: {
        'Material': '100% Polypropylene',
        'Construction': 'Woven Fabric',
        'Weight Range': '10kg - 100kg',
        'Dimensions': 'Customizable as per requirement',
        'Printing': 'Flexo/Offset printing available',
        'Colors': 'Natural, White, or Custom colors',
        'Finish': 'Standard or UV treated'
      },
      applications: [
        'Agriculture (Fertilizers, Seeds)',
        'Construction Materials',
        'Chemical Products',
        'Consumer Goods',
        'Industrial Products',
        'Bulk Commodities'
      ],
      manufacturing: [
        'PP Tape Extrusion',
        'Fabric Weaving',
        'Cutting & Stitching',
        'Printing (if required)',
        'Quality Control',
        'Packaging & Dispatch'
      ]
    },
    'laminated-woven-bags': {
      title: 'Laminated Woven Bags',
      subtitle: 'Extrusion coated for superior moisture barrier protection',
      description: 'Essential for Cement, Grain, and Fertilizers to protect contents from environmental factors. Our Laminated Woven Bags provide the perfect balance of strength and protection.',
      images: [
        { id: 1, src: laminatedWovenBags1, alt: 'Laminated Woven Bag 1' },
        { id: 2, src: laminatedWovenBags2, alt: 'Laminated Woven Bag 2' },
        { id: 3, src: laminatedWovenBags3, alt: 'Laminated Woven Bag 3' }
      ],
      benefits: [
        {
          title: 'Moisture Barrier',
          description: 'Extrusion coating provides excellent moisture protection, keeping contents dry and safe.',
          icon: 'fas fa-shield-alt'
        },
        {
          title: 'Environmental Protection',
          description: 'Protects contents from humidity, dust, and other environmental factors during storage and transport.',
          icon: 'fas fa-leaf'
        },
        {
          title: 'High Strength',
          description: 'Combines woven fabric strength with protective coating for maximum durability.',
          icon: 'fas fa-dumbbell'
        },
        {
          title: 'Weather Resistant',
          description: 'Excellent resistance to UV rays and weather conditions for outdoor applications.',
          icon: 'fas fa-cloud-sun'
        },
        {
          title: 'Cost Effective',
          description: 'Eliminates need for additional liners, providing cost savings while maintaining protection.',
          icon: 'fas fa-dollar-sign'
        },
        {
          title: 'Versatile Applications',
          description: 'Suitable for various industries requiring moisture-sensitive product protection.',
          icon: 'fas fa-industry'
        }
      ],
      specifications: {
        'Material': 'PP Woven + PE Coating',
        'Coating': 'Extrusion Lamination',
        'Weight Range': '25kg - 100kg',
        'Dimensions': 'Customizable as per requirement',
        'Moisture Barrier': 'Excellent',
        'UV Resistance': 'Available',
        'Printing': 'Flexo printing available'
      },
      applications: [
        'Cement Packaging',
        'Grain Storage',
        'Fertilizer Packaging',
        'Chemical Products',
        'Food Grains',
        'Industrial Materials'
      ],
      manufacturing: [
        'PP Tape Extrusion',
        'Fabric Weaving',
        'PE Extrusion Coating',
        'Cutting & Stitching',
        'Printing (if required)',
        'Quality Control',
        'Packaging & Dispatch'
      ]
    },
    'fibc': {
      title: 'FIBC (Flexible Intermediate Bulk Containers)',
      subtitle: 'Large capacity bulk packaging solutions',
      description: 'Flexible Intermediate Bulk Containers for various industries. Our FIBC bags are designed for efficient handling and storage of bulk materials.',
      images: [
        { id: 1, src: fibcBags1, alt: 'FIBC Bag 1' },
        { id: 2, src: fibcBags2, alt: 'FIBC Bag 2' },
        { id: 3, src: fibcBags3, alt: 'FIBC Bag 3' }
      ],
      benefits: [
        {
          title: 'Large Capacity',
          description: 'Designed to handle bulk quantities efficiently, reducing packaging and handling costs.',
          icon: 'fas fa-boxes'
        },
        {
          title: 'Easy Handling',
          description: 'Forklift-friendly design with lifting loops for easy transportation and storage.',
          icon: 'fas fa-truck'
        },
        {
          title: 'Space Efficient',
          description: 'Collapsible design saves storage space when empty, optimizing warehouse utilization.',
          icon: 'fas fa-compress-arrows-alt'
        },
        {
          title: 'Customizable',
          description: 'Available in various sizes, configurations, and special features as per requirements.',
          icon: 'fas fa-cogs'
        },
        {
          title: 'Durable',
          description: 'High-quality construction ensures long-lasting performance in demanding environments.',
          icon: 'fas fa-shield-alt'
        },
        {
          title: 'Cost Effective',
          description: 'Reduces overall packaging costs through efficient bulk handling and storage.',
          icon: 'fas fa-dollar-sign'
        }
      ],
      specifications: {
        'Material': 'PP Woven Fabric',
        'Capacity': '500kg - 2000kg',
        'Loops': '1 Loop, 2 Loop, 4 Loop options',
        'Liner': 'PE Liner available',
        'UV Treatment': 'Available',
        'Anti-Static': 'Available for specific applications',
        'UN Certified': 'Available for hazardous materials'
      },
      applications: [
        'Chemical Industry',
        'Food & Agriculture',
        'Mining & Minerals',
        'Construction Materials',
        'Pharmaceuticals',
        'Plastic Granules'
      ],
      manufacturing: [
        'PP Tape Extrusion',
        'Fabric Weaving',
        'Loop Attachment',
        'Bottom Sealing',
        'Liner Installation (if required)',
        'Quality Control & Testing',
        'Packaging & Dispatch'
      ]
    },
    'pp-rolls': {
      title: 'PP Rolls',
      subtitle: 'Woven polypropylene rolls for packaging conversion and industrial applications',
      description: 'PP Rolls are high-performance woven polypropylene roll stock used as a base substrate for bag making, lamination, and custom converting. They are engineered for consistent weaving, processability, and strength across high-speed production environments.',
      images: [
        { id: 1, src: ppRolls1, alt: 'PP Roll 1' },
        { id: 2, src: ppRolls2, alt: 'PP Roll 2' },
        { id: 3, src: ppRolls3, alt: 'PP Roll 3' }
      ],
      benefits: [
        {
          title: 'High Tensile Strength',
          description: 'Manufactured with quality PP tapes to deliver strong and reliable roll performance for downstream conversion.',
          icon: 'fas fa-dumbbell'
        },
        {
          title: 'Uniform Weave Structure',
          description: 'Consistent weaving supports better converting efficiency and predictable quality outcomes.',
          icon: 'fas fa-ruler-combined'
        },
        {
          title: 'Conversion Ready',
          description: 'Suitable for lamination, printing, cutting, and stitching processes across multiple packaging lines.',
          icon: 'fas fa-cogs'
        },
        {
          title: 'Custom Width Options',
          description: 'Available in different widths and specifications to match application-specific requirements.',
          icon: 'fas fa-arrows-alt-h'
        },
        {
          title: 'Durable in Handling',
          description: 'Designed to withstand transport, storage, and machine handling during high-volume operations.',
          icon: 'fas fa-shield-alt'
        },
        {
          title: 'Cost Effective',
          description: 'Optimized roll format helps improve process productivity and reduce conversion waste.',
          icon: 'fas fa-dollar-sign'
        }
      ],
      specifications: {
        'Material': '100% Polypropylene',
        'Form': 'Woven Roll Stock',
        'Width Range': 'Customizable as per requirement',
        'GSM': 'As per product specification',
        'Color': 'Natural, White, and Custom options',
        'Treatment': 'UV treatment available',
        'Print/Lamination': 'Suitable for printing and lamination'
      },
      applications: [
        'Bag Conversion Units',
        'Lamination Base Fabric',
        'Agriculture Packaging',
        'Cement and Chemical Packaging',
        'Industrial Packaging',
        'Custom Woven Converting'
      ],
      manufacturing: [
        'PP Tape Extrusion',
        'Precision Weaving Process',
        'Roll Winding and Alignment',
        'Inspection and GSM Verification',
        'Edge and Width Quality Check',
        'Packing and Dispatch'
      ]
    }
  };

  const product = productData[productId];

  if (!product) {
    return (
      <div className="App">
        <Header />
        <div className="product-not-found">
          <h2>Product Not Found</h2>
          <Link to="/products" className="btn btn-primary">Back to Products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="product-detail-page">
        <div className="container">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/products">Product Type</Link>
            <span>/</span>
            <span>{product.title}</span>
          </div>

          {/* Product Header */}
          <div className="product-header">
            <h1>{product.title}</h1>
            <p className="product-subtitle">{product.subtitle}</p>
          </div>

          {/* Product Images */}
          <div className="product-images">
            <div className="main-image">
              <img
                src={product.images[selectedImageIndex].src}
                alt={product.images[selectedImageIndex].alt}
                className="main-product-image"
              />
              <div className="main-image-caption">
                <h4>{product.images[selectedImageIndex].alt}</h4>
                <p>{getThumbnailInsight(product, selectedImageIndex)}</p>
              </div>
            </div>
            <div className="thumbnail-grid">
              {product.images.map((image, index) => (
                <div
                  key={image.id}
                  className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedImageIndex(index);
                    }
                  }}
                >
                  <img src={image.src} alt={image.alt} className="thumbnail-image" />
                  <div className="thumbnail-content">
                    <h4>{image.alt}</h4>
                    <p>{getThumbnailInsight(product, index)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product Description */}
          <div className="product-description">
            <h2>Introduction</h2>
            <p>{product.description}</p>
          </div>

          {/* Tabs */}
          <div className="product-tabs">
            <div className="tab-buttons">
              <button 
                className={`tab-button ${activeTab === 'benefits' ? 'active' : ''}`}
                onClick={() => setActiveTab('benefits')}
              >
                Benefits & Details
              </button>
              <button 
                className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
                onClick={() => setActiveTab('specifications')}
              >
                Types & Specification
              </button>
              <button 
                className={`tab-button ${activeTab === 'manufacturing' ? 'active' : ''}`}
                onClick={() => setActiveTab('manufacturing')}
              >
                Manufacturing Process
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'benefits' && (
                <div className="benefits-section">
                  <h3>Benefits</h3>
                  <div className="benefits-grid">
                    {product.benefits.map((benefit, index) => (
                      <div key={index} className="benefit-card">
                        <div className="benefit-icon">
                          <i className={benefit.icon}></i>
                        </div>
                        <div className="benefit-content">
                          <h4>{benefit.title}</h4>
                          <p>{benefit.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'specifications' && (
                <div className="specifications-section">
                  <h3>Specifications</h3>
                  <div className="specs-grid">
                    {Object.entries(product.specifications).map(([key, value], index) => (
                      <div key={index} className="spec-item">
                        <span className="spec-label">{key}:</span>
                        <span className="spec-value">{value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <h3>Applications</h3>
                  <div className="applications-grid">
                    {product.applications.map((app, index) => (
                      <div key={index} className="application-tag">
                        {app}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'manufacturing' && (
                <div className="manufacturing-section">
                  <h3>Manufacturing Process</h3>
                  <div className="process-steps">
                    {product.manufacturing.map((step, index) => (
                      <div key={index} className="process-step">
                        <div className="step-number">{index + 1}</div>
                        <div className="step-content">
                          <p>{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Section */}
          <div className="product-contact">
            <div className="contact-content">
              <h3>Interested in this product?</h3>
              <p>Get in touch with us for detailed specifications, pricing, and custom solutions.</p>
              <div className="contact-actions">
                <Link to="/contact" className="btn btn-primary">Contact Us</Link>
                <Link to="/products" className="btn btn-secondary">View All Products</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
